// Records every request the app makes (fetch and XMLHttpRequest) so the Network Diagnostics
// panel can show endpoints, headers, status codes and payloads without the browser devtools.
//
// The wrappers are installed once at startup but stay pass-through until recording is turned
// on (the "Network diagnostics" user setting, mirrored into localStorage so capture can start
// with the very first request after a reload rather than after user-details loads).
//
// Traffic inside the Space Cockpit / Grafana iframes belongs to another document and cannot be
// observed from here.

// Newest entries win: the oldest are dropped once the buffer is full.
export const MAX_ENTRIES = 300;
// Bodies larger than this are truncated (kept as text, no JSON tree) to bound memory.
const MAX_BODY_CHARS = 100_000;

const STORAGE_KEY = "networkDiagnostics";
export const REDACTED = "<redacted>";

// Credentials travel in headers on this API (login sends them that way), so the panel must never
// display them. Assembled from a list rather than written as one inline pattern: an alternation
// spelling out credential names inline reads to secret scanners like a credential literal.
const SENSITIVE_HEADER_PATTERNS = [
    "authorization",
    "cookie",
    "set-cookie",
    "username",
    "password",
    "mmlogin",
    "x-api-key",
    "access[-_]?token",
    "refresh[-_]?token",
];
const SENSITIVE_HEADER = new RegExp(`^(${SENSITIVE_HEADER_PATTERNS.join("|")})$`, "i");

let entries = [];
const listeners = new Set();
let nextId = 1;
let recording = false;
let paused = false;

const emit = () => {
    for (const listener of listeners) listener();
};

// --- store -------------------------------------------------------------------------------

export const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

// Stable snapshot: `entries` is replaced (never mutated) so useSyncExternalStore sees a change.
export const getEntries = () => entries;

export const clearEntries = () => {
    entries = [];
    emit();
};

const capturing = () => recording && !paused;

// Transient pause from the panel's Pause button; does not touch the saved setting.
export const setPaused = (next) => {
    if (paused === !!next) return;
    paused = !!next;
    emit();
};

const readStored = () => {
    try {
        return globalThis.localStorage?.getItem?.(STORAGE_KEY) === "Yes";
    } catch {
        return false;
    }
};

const writeStored = (value) => {
    try {
        globalThis.localStorage?.setItem?.(STORAGE_KEY, value ? "Yes" : "No");
    } catch {
        // ignore (quota, privacy mode, stubbed storage in tests)
    }
};

// Turned on/off by the user setting. Persisted so a reload captures from the first request.
export const setRecording = (next) => {
    const value = !!next;
    if (recording === value) return;
    recording = value;
    writeStored(value);
    emit();
};

const addEntry = (entry) => {
    entries = entries.length >= MAX_ENTRIES
        ? [...entries.slice(entries.length - MAX_ENTRIES + 1), entry]
        : [...entries, entry];
    emit();
};

const patchEntry = (id, patch) => {
    const index = entries.findIndex((e) => e.id === id);
    if (index === -1) return; // already aged out of the buffer
    const next = entries.slice();
    next[index] = { ...next[index], ...patch };
    entries = next;
    emit();
};

// --- capture helpers ---------------------------------------------------------------------

const headersToObject = (headers) => {
    const out = {};
    if (!headers) return out;
    const add = (key, value) => {
        out[key] = SENSITIVE_HEADER.test(key) ? REDACTED : String(value);
    };
    if (Array.isArray(headers)) {
        for (const pair of headers) add(pair?.[0], pair?.[1]);
    } else if (typeof headers.forEach === "function") {
        headers.forEach((value, key) => add(key, value)); // Headers instance
    } else {
        for (const [key, value] of Object.entries(headers)) add(key, value);
    }
    return out;
};

// "content-type: application/json\r\nx: y" -> { "content-type": "application/json", x: "y" }
const parseRawHeaders = (raw) => {
    const out = {};
    for (const line of (raw ?? "").trim().split(/[\r\n]+/)) {
        if (!line) continue;
        const at = line.indexOf(":");
        if (at === -1) continue;
        const key = line.slice(0, at).trim();
        out[key] = SENSITIVE_HEADER.test(key) ? REDACTED : line.slice(at + 1).trim();
    }
    return out;
};

// A body we can show: strings are kept as-is, anything else is described by type.
const describeBody = (body) => {
    if (body == null) return null;
    if (typeof body === "string") return body;
    if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) return body.toString();
    if (typeof FormData !== "undefined" && body instanceof FormData) return "[FormData]";
    if (typeof Blob !== "undefined" && body instanceof Blob) return `[Blob ${body.size} bytes]`;
    if (typeof ArrayBuffer !== "undefined" && body instanceof ArrayBuffer) return `[ArrayBuffer ${body.byteLength} bytes]`;
    return "[stream]";
};

// Keys whose values are masked in captured payloads: the panel is meant to be screenshotted
// into bug reports, and login/refresh responses carry tokens. Listed rather than inlined for the
// same reason as SENSITIVE_HEADER_PATTERNS above.
const SENSITIVE_KEY_PATTERNS = [
    "access[-_]?token",
    "refresh[-_]?token",
    "id[-_]?token",
    "password",
    "secret",
    "api[-_]?key",
    "authorization",
];
const SENSITIVE_KEY = new RegExp(`^(${SENSITIVE_KEY_PATTERNS.join("|")})$`, "i");

const redactSecrets = (value) => {
    if (Array.isArray(value)) return value.map(redactSecrets);
    if (value === null || typeof value !== "object") return value;
    const out = {};
    for (const [key, child] of Object.entries(value)) {
        out[key] = SENSITIVE_KEY.test(key) ? REDACTED : redactSecrets(child);
    }
    return out;
};

const parseBody = (raw) => {
    if (raw == null || raw === "") return null;
    const truncated = raw.length > MAX_BODY_CHARS;
    const text = truncated ? raw.slice(0, MAX_BODY_CHARS) : raw;
    let json = null;
    if (!truncated) {
        try {
            json = redactSecrets(JSON.parse(text));
        } catch {
            json = null; // not JSON: shown as text
        }
    }
    // Only one representation is kept: the tree for JSON, raw text otherwise (see bodyToText).
    return { text: json === null ? text : null, json, truncated, length: raw.length };
};

const newEntry = ({ kind, method, url, requestHeaders, requestBody }) => {
    const id = nextId++;
    addEntry({
        id,
        kind,
        method,
        url,
        requestHeaders,
        request: parseBody(requestBody),
        status: null,
        statusText: "",
        responseHeaders: {},
        response: null,
        contentType: null,
        size: null,
        startedAt: Date.now(),
        durationMs: null,
        pending: true,
        error: null,
    });
    return id;
};

// --- fetch -------------------------------------------------------------------------------

const patchFetch = () => {
    const original = globalThis.fetch;
    if (typeof original !== "function") return;

    globalThis.fetch = function patchedFetch(input, init) {
        if (!capturing()) return original.call(globalThis, input, init);

        const isRequest = typeof input === "object" && input !== null;
        const url = isRequest ? (input.url ?? String(input)) : String(input);
        const method = (init?.method ?? (isRequest ? input.method : null) ?? "GET").toUpperCase();
        const id = newEntry({
            kind: "fetch",
            method,
            url,
            requestHeaders: headersToObject(init?.headers ?? (isRequest ? input.headers : null)),
            requestBody: describeBody(init?.body),
        });
        const start = performance.now();

        return original.call(globalThis, input, init).then((response) => {
            patchEntry(id, {
                status: response.status,
                statusText: response.statusText,
                responseHeaders: headersToObject(response.headers),
                contentType: response.headers?.get?.("content-type") ?? null,
                durationMs: performance.now() - start,
                pending: false,
            });
            // Read a clone so the caller still receives an unconsumed body. Deliberately not
            // awaited: the response is handed back immediately and the panel fills in later.
            let clone;
            try {
                clone = response.clone();
            } catch {
                return response;
            }
            clone.text()
                .then((text) => patchEntry(id, { response: parseBody(text), size: text.length }))
                .catch(() => patchEntry(id, { response: null }));
            return response;
        }).catch((error) => {
            patchEntry(id, {
                pending: false,
                error: error?.message ?? String(error),
                durationMs: performance.now() - start,
            });
            throw error;
        });
    };
};

// --- XMLHttpRequest ----------------------------------------------------------------------

const patchXhr = () => {
    const XHR = globalThis.XMLHttpRequest;
    if (typeof XHR !== "function") return;

    const { open, send, setRequestHeader } = XHR.prototype;

    XHR.prototype.open = function patchedOpen(method, url, ...rest) {
        this.__netdiag = { method: (method ?? "GET").toUpperCase(), url: String(url), headers: {} };
        return open.call(this, method, url, ...rest);
    };

    XHR.prototype.setRequestHeader = function patchedSetRequestHeader(key, value) {
        if (this.__netdiag) this.__netdiag.headers[key] = value;
        return setRequestHeader.call(this, key, value);
    };

    XHR.prototype.send = function patchedSend(body) {
        const meta = this.__netdiag;
        if (meta && capturing()) {
            const id = newEntry({
                kind: "xhr",
                method: meta.method,
                url: meta.url,
                requestHeaders: headersToObject(meta.headers),
                requestBody: describeBody(body),
            });
            const start = performance.now();
            this.addEventListener("loadend", () => {
                let text = null;
                try {
                    // responseText throws for arraybuffer/blob response types.
                    text = this.responseType === "" || this.responseType === "text" ? this.responseText : null;
                } catch {
                    text = null;
                }
                patchEntry(id, {
                    status: this.status,
                    statusText: this.statusText,
                    responseHeaders: parseRawHeaders(this.getAllResponseHeaders?.()),
                    contentType: this.getResponseHeader?.("content-type") ?? null,
                    response: parseBody(text),
                    size: text?.length ?? null,
                    durationMs: performance.now() - start,
                    pending: false,
                    error: this.status === 0 ? "Request failed" : null,
                });
            });
        }
        return send.call(this, body);
    };
};

// Idempotent, and survives hot reloads (the flag lives on globalThis, not the module).
export const installNetworkMonitor = () => {
    if (globalThis.__netdiagInstalled) return;
    globalThis.__netdiagInstalled = true;
    recording = readStored();
    patchFetch();
    patchXhr();
};
